import { useState, useEffect } from 'react';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { DataProvider } from './contexts/DataContext';
import LoginForm from './components/LoginForm';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import TendersList from './components/TendersList';
import OrganizationsList from './components/OrganizationsList';
import GenericCrud from './components/GenericCrud';
import { useData } from './contexts/DataContext';
import ProductsList from './components/ProductsList';
import CurrencyRatesList from './components/CurrencyRatesList';
import EmployeesList from './components/EmployeesList';
import CompaniesList from './components/CompaniesList';
import GlobalEmployeesList from './components/GlobalEmployeesList';
import PlansList from './components/PlansList';
import TenderCalendar from './components/TenderCalendar';
import ApplicationsKanban from './components/ApplicationsKanban';
import AccountSettings from './components/AccountSettings';
import CurrenciesTable from './components/CurrenciesTable';
import OnboardingModal from './components/OnboardingModal';
import { Tag } from 'lucide-react';

const TenderTypesCrud = () => {
  const { tenderTypes, addTenderType, updateTenderType, deleteTenderType } = useData();
  
  return (
    <GenericCrud
      title="Tipos de Licitación"
      subtitle="Gestiona los tipos de licitaciones disponibles"
      items={tenderTypes}
      onAdd={addTenderType}
      onUpdate={updateTenderType}
      onDelete={deleteTenderType}
      fields={[
        { key: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'Ej: Construcción de obra pública' },
        { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Descripción del tipo de licitación' },
        { 
          key: 'category', 
          label: 'Categoría', 
          type: 'select', 
          required: true,
          options: [
            { value: 'goods', label: 'Bienes' },
            { value: 'services', label: 'Servicios' },
            { value: 'works', label: 'Obras' },
            { value: 'consultancy', label: 'Consultoría' }
          ]
        }
      ]}
      displayFields={[
        { key: 'category', label: 'Categoría', type: 'badge' }
      ]}
    />
  );
};

const TenderStatusesCrud = () => {
  const { tenderStatuses, addTenderStatus, updateTenderStatus, deleteTenderStatus } = useData();
  const { currentCompany } = useTenant();
  
  // Solo Licitatrak puede gestionar estados globales
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';
  
  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h3>
        <p className="text-gray-600">
          Los estados de licitación son globales y solo pueden ser gestionados por Licitatrak.
          Todos los tenants comparten los mismos estados de licitación.
        </p>
      </div>
    );
  }
  
  return (
    <GenericCrud
      title="Estados de Licitación (Global)"
      subtitle="Gestiona los estados globales del ciclo de vida de las licitaciones para todos los tenants"
      items={tenderStatuses}
      onAdd={addTenderStatus}
      onUpdate={updateTenderStatus}
      onDelete={deleteTenderStatus}
      fields={[
        { key: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'Ej: Publicada' },
        { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Descripción del estado' },
        { key: 'color', label: 'Color', type: 'color', required: true }
      ]}
      displayFields={[
        { key: 'color', label: 'Color', type: 'color' }
      ]}
    />
  );
};

const ApplicationStatusesCrud = () => {
  const { applicationStatuses, addApplicationStatus, updateApplicationStatus, deleteApplicationStatus } = useData();
  const { currentCompany } = useTenant();
  
  // Solo Licitatrak puede gestionar estados globales
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';
  
  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h3>
        <p className="text-gray-600">
          Los estados de postulación son globales y solo pueden ser gestionados por Licitatrak.
          Todos los tenants comparten los mismos estados de postulación.
        </p>
      </div>
    );
  }
  
  return (
    <GenericCrud
      title="Estados de Postulación (Global)"
      subtitle="Gestiona los estados globales del flujo de postulaciones para todos los tenants"
      items={applicationStatuses}
      onAdd={addApplicationStatus}
      onUpdate={updateApplicationStatus}
      onDelete={deleteApplicationStatus}
      fields={[
        { key: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'Ej: En Análisis' },
        { key: 'description', label: 'Descripción', type: 'textarea', placeholder: 'Descripción del estado' },
        { key: 'color', label: 'Color', type: 'color', required: true },
        { key: 'order_index', label: 'Orden', type: 'number', required: true, placeholder: '1' }
      ]}
      displayFields={[
        { key: 'color', label: 'Color', type: 'color' },
        { key: 'order_index', label: 'Orden' }
      ]}
    />
  );
};

const AppContent = () => {
  const { currentCompany, currentUser, currentCompanyUser, isLoading } = useTenant();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Verificar si debe mostrar el onboarding
  useEffect(() => {
    if (currentCompany && currentUser && !isLoading) {
      console.log('Current company user:', currentCompanyUser);
      if (currentCompanyUser && !currentCompanyUser.Onboarding1) {
        console.log('Mostrando onboarding principal');
        setShowOnboarding(true);
      }
    }
  }, [currentCompany, currentUser, currentCompanyUser, isLoading]);

  // Mostrar loading mientras se inicializa el sistema
  if (isLoading) {
    return <LoginForm />;
  }

  // Si no hay empresa o usuario después de cargar, mostrar login
  if (!currentCompany || !currentUser) {
    return <LoginForm />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'tenders':
        return <TendersList />;
      case 'applications-kanban':
        return <ApplicationsKanban />;
      case 'organizations':
        return <OrganizationsList />;
      case 'tender-types':
        return <TenderTypesCrud />;
      case 'tender-statuses':
        return <TenderStatusesCrud />;
      case 'application-statuses':
        return <ApplicationStatusesCrud />;
      case 'products':
        return <ProductsList />;
      case 'currency-rates':
        return <CurrencyRatesList />;
      case 'currencies':
        return <CurrenciesTable />;
      case 'employees':
        return <EmployeesList />;
      case 'companies':
        return <CompaniesList />;
      case 'global-employees':
        return <GlobalEmployeesList />;
      case 'plans':
        return <PlansList />;
      case 'calendar':
        return <TenderCalendar onPageChange={setCurrentPage} />;
      case 'account':
        return <AccountSettings />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <DataProvider>
      <Layout currentPage={currentPage} onPageChange={setCurrentPage}>
        {renderPage()}
      </Layout>
      
      {/* Modal de onboarding */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />
    </DataProvider>
  );
};

function App() {
  return (
    <TenantProvider>
      <AppContent />
    </TenantProvider>
  );
}

export default App;